import Link from 'next/link';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useRouter } from 'next/router';
import { Routes } from 'routes';
import { loadZendesk } from 'services/zendesk';
import { NavContainer, NavLink, NavLinkContainer, NavText } from './styles';

const navSections = [
  {
    heading: 'Cover',
    links: [
      { text: 'Car insurance', url: Routes.CAR_INSURANCE, migrated: true },
      { text: 'Contents insurance', url: Routes.HOME_CONTENTS_INSURANCE, migrated: true },
      { text: 'Building insurance', url: Routes.BUILDING_INSURANCE, migrated: true },
      { text: 'Single item insurance', url: Routes.SINGLE_ITEMS_INSURANCE, migrated: true },
      { text: 'Pet insurance', url: Routes.PET_INSURANCE, migrated: true },
      { text: 'Phone insurance', url: Routes.PHONE_INSURANCE, migrated: true },
      { text: 'Caravan & trailer insurance', url: Routes.QuoteTowable, migrated: true },
    ],
  },
  {
    heading: 'Resources',
    links: [
      { text: 'Blog', url: Routes.BLOG, migrated: true },
      { text: 'Car insurance guide', url: Routes.CAR_CONTENT_HUB, migrated: true },
      {
        text: 'Building calculator',
        url: Routes.BUILDING_VALUE_CALCULATOR,
        migrated: true,
      },
      { text: 'FAQs', url: Routes.FAQ, migrated: true },
      { text: 'Legal stuff', url: Routes.TERMS_OF_USE, migrated: true },
    ],
  },
  {
    heading: 'Company',
    links: [
      { text: 'About us', url: Routes.ABOUT_US, migrated: true },
      { text: 'Naked Difference', url: Routes.Difference, migrated: true },
      { text: 'Team Naked', url: Routes.CAREERS_AT_NAKED, migrated: true },
      { text: 'Contact us', url: Routes.CONTACT_US, migrated: true },
    ],
  },
];

/**
 * `FooterNav` is a functional component that renders a navigation bar in the footer.
 * The navigation bar is displayed as a grid with two columns on screens smaller than 1024px and three columns on larger screens.
 * If the `migrated` property of a link is true, the link is rendered as a normal anchor tag. Otherwise, it is rendered as a Next.js Link component.
 *
 * @example
 * return (
 *   <FooterNav />
 * )
 */
export function FooterNav() {
  const [isZendeskOpen, setIsZendeskOpen] = useState(false);
  const router = useRouter();
  const footerRef = useRef<HTMLDivElement | null>(null);

  const openZendesk = useCallback(() => {
    if (typeof window !== 'undefined' && typeof window.zE === 'function') {
      window.zE('webWidget', 'open');
      setIsZendeskOpen(true);
    }
  }, []);

  const hideZendesk = useCallback(() => {
    if (typeof window !== 'undefined' && typeof window.zE === 'function') {
      window.zE('webWidget', 'hide');
      setIsZendeskOpen(false);
    }
  }, []);

  useEffect(() => {
    // Only enable the basket-specific behavior on the Basket route and in the browser
    if (typeof window === 'undefined') {
      return;
    }

    const el = footerRef.current;

    if (!el) {
      return;
    }

    if (typeof window.zE === 'function') {
      window.zE('webWidget:on', 'open', () => {
        setIsZendeskOpen(true);
      });

      window.zE('webWidget:on', 'close', () => {
        setIsZendeskOpen(false);
      });
    }

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        const onBasket = router.pathname === Routes.BASKET;

        if (!onBasket || !entry) {
          return;
        }

        if (!entry.isIntersecting) {
          hideZendesk();
        } else if (entry.isIntersecting) {
          loadZendesk();
        }
      },
      { threshold: 0 }
    );

    if (!isZendeskOpen) {
      observer.observe(el);
    }

    return () => observer.disconnect();
  }, [router.pathname, isZendeskOpen, hideZendesk]);

  return (
    <NavContainer ref={footerRef}>
      {navSections.map(({ heading, links }) => (
        <NavLinkContainer key={heading}>
          <NavText>{heading}</NavText>

          {links.map(({ text, url, migrated }) => {
            if (url === Routes.CONTACT_US) {
              return (
                <NavLink key={text} onClick={openZendesk}>
                  {text}
                </NavLink>
              );
            }

            if (migrated) {
              return (
                <NavLink key={text} href={url}>
                  {text}
                </NavLink>
              );
            } else {
              return (
                <Link key={text} href={url} prefetch={false}>
                  <NavLink href={url}>{text}</NavLink>
                </Link>
              );
            }
          })}
        </NavLinkContainer>
      ))}
    </NavContainer>
  );
}
